/**
 * Knowledge files of a project, sent straight to the Go backend via
 * `POST /api/ces/projects/{project_id}/knowledge-files`.
 *
 * `uploadContextFile` in `api/projects.ts` posts to the same path through the
 * Backstage proxy, which does not forward the session cookie. This one rides
 * `apiCall` like the project list does, so the upload is authenticated the
 * same way as the list that feeds the project picker.
 */

import { apiCall, API_BASE_URL } from '../utils/apiClient';
import type {
  ContextFileStatus,
  ContextFileTypeTag,
} from '../components/projects/types';

// A 10 MB file is ~13 MB of base64 in one request; the default 60 s is too
// tight on a slow connection.
const UPLOAD_TIMEOUT_MS = 5 * 60 * 1000;

export interface UploadKnowledgeFileResult {
  fileId: string;
  status: ContextFileStatus;
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  // Spreading a large array into String.fromCharCode overflows the call stack.
  const chunkSize = 0x8000;
  let binary = '';
  for (let index = 0; index < bytes.length; index += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(index, index + chunkSize));
  }
  return btoa(binary);
}

export async function uploadKnowledgeFile(
  projectId: number,
  file: File,
  typeTag: ContextFileTypeTag,
): Promise<UploadKnowledgeFileResult> {
  const fileContent = arrayBufferToBase64(await file.arrayBuffer());

  const response = await apiCall(
    `${API_BASE_URL}/projects/${projectId}/knowledge-files`,
    {
      method: 'POST',
      body: JSON.stringify({
        file_name: file.name,
        content_type: file.type || 'application/octet-stream',
        file_content: fileContent,
        type_tag: typeTag,
      }),
    },
    UPLOAD_TIMEOUT_MS,
  );

  const data = (await response.json()) as { id: string; status: string };
  return { fileId: data.id, status: data.status as ContextFileStatus };
}
